/**
 * useChunkedUpload - React hook for chunked file uploads with S3 multipart
 * Supports files up to 100GB with:
 * - Resumable uploads
 * - Progress tracking
 * - Pause/resume capability
 * - Automatic retry on failure
 * - Adaptive chunk sizing (ramps up for throughput)
 * - Non-sticky proxy fallback (per-upload only)
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

import { API_BASE } from '../lib/apiBase';
const API = API_BASE + '/api';

/**
 * Adaptive upload tiers - chunk size and concurrency ramp up as upload progresses
 * This provides:
 * - Faster UI responsiveness at start (smaller chunks = quicker progress updates)
 * - Maximum throughput for bulk of large files (larger chunks = less overhead)
 * - Smoother user experience overall
 */
const ADAPTIVE_TIERS = [
    {
        name: 'ramp-up',
        uploadedThreshold: 0,                    // 0 - 100MB uploaded
        chunkSize: 10 * 1024 * 1024,             // 10MB chunks
        maxConcurrent: 6,                         // 6 parallel
        description: 'Initial ramp-up for responsiveness'
    },
    {
        name: 'cruising',
        uploadedThreshold: 100 * 1024 * 1024,    // 100MB - 500MB uploaded
        chunkSize: 25 * 1024 * 1024,             // 25MB chunks
        maxConcurrent: 8,                         // 8 parallel
        description: 'Balanced throughput'
    },
    {
        name: 'turbo',
        uploadedThreshold: 500 * 1024 * 1024,    // 500MB+ uploaded
        chunkSize: 50 * 1024 * 1024,             // 50MB chunks
        maxConcurrent: 10,                        // 10 parallel
        description: 'Maximum throughput'
    }
];

/**
 * Get the current adaptive tier based on bytes already uploaded
 */
export function getAdaptiveTier(uploadedBytes) {
    // Find the highest tier we qualify for
    let tier = ADAPTIVE_TIERS[0];
    for (const t of ADAPTIVE_TIERS) {
        if (uploadedBytes >= t.uploadedThreshold) {
            tier = t;
        }
    }
    return tier;
}

/**
 * Legacy profile getter for compatibility - uses initial tier
 */
export function getUploadProfile(fileSize) {
    // For small files, use small chunks throughout
    if (fileSize <= 50 * 1024 * 1024) {
        return {
            name: 'small',
            chunkSize: 10 * 1024 * 1024,
            maxConcurrent: 3,
            adaptive: false
        };
    }
    // For larger files, start with adaptive tier 0
    return {
        name: 'adaptive',
        chunkSize: ADAPTIVE_TIERS[0].chunkSize,
        maxConcurrent: ADAPTIVE_TIERS[0].maxConcurrent,
        adaptive: true
    };
}

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

/**
 * IMMUTABLE chunk size for a whole upload session.
 *
 * A single, fixed chunk size is chosen once from the file size and used for
 * every part AND every resume. Part N therefore always maps to the exact byte
 * range [(N-1)*chunkSize, min(N*chunkSize, fileSize)), which makes the final
 * S3 assembly impossible to corrupt via overlaps/gaps. Sizes stay >= 5MB (S3
 * minimum part size) and keep the part count well under S3's 10000 limit.
 */
export function getFixedChunkSize(fileSize) {
    if (fileSize <= 50 * 1024 * 1024) return 10 * 1024 * 1024;         // <=50MB  -> 10MB
    if (fileSize <= 5 * 1024 * 1024 * 1024) return 25 * 1024 * 1024;   // <=5GB   -> 25MB
    if (fileSize <= 50 * 1024 * 1024 * 1024) return 64 * 1024 * 1024;  // <=50GB  -> 64MB
    return 100 * 1024 * 1024;                                         // larger  -> 100MB (S3/backend max)
}

/**
 * Concurrency is independent of the chunk plan (it never changes byte ranges),
 * so it can scale with file size for throughput without affecting integrity.
 */
export function getConcurrency(fileSize) {
    if (fileSize <= 50 * 1024 * 1024) return 3;
    if (fileSize <= 500 * 1024 * 1024) return 6;
    return 8;
}

// Upload statuses
export const UPLOAD_STATUS = {
    QUEUED: 'queued',
    UPLOADING: 'uploading',
    PAUSED: 'paused',
    RESUMING: 'resuming',
    COMPLETING: 'completing',
    COMPLETED: 'completed',
    FAILED: 'failed',
    ABORTED: 'aborted'
};

/**
 * Format file size for display
 */
export function formatFileSize(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Calculate upload speed
 */
function calculateSpeed(startTime, bytesUploaded) {
    const elapsed = (Date.now() - startTime) / 1000; // seconds
    if (elapsed === 0) return 0;
    return bytesUploaded / elapsed; // bytes per second
}

/**
 * Calculate ETA
 */
function calculateETA(speed, remainingBytes) {
    if (speed === 0) return null;
    const seconds = remainingBytes / speed;
    if (seconds < 60) return `${Math.ceil(seconds)}s`;
    if (seconds < 3600) return `${Math.ceil(seconds / 60)}m`;
    return `${Math.ceil(seconds / 3600)}h`;
}

/**
 * Main chunked upload hook
 */
export function useChunkedUpload(orderId, category, onComplete, onMD5AutoRetryComplete) {
    const [uploads, setUploads] = useState([]);
    const uploadRefs = useRef(new Map()); // Store upload state refs
    const abortControllers = useRef(new Map()); // Store abort controllers
    const statusRefs = useRef(new Map()); // Track status without closure issues
    // Automatic MD5 retry: fires POST /master-files/auto-retry-missing-md5
    // once per settled batch. `autoRetryTimerRef` debounces so a batch of
    // uploads finishing in the same tick only triggers a single call, and
    // `autoRetryLastKeyRef` prevents re-firing until a NEW upload joins
    // the list (i.e. a fresh batch begins).
    const autoRetryTimerRef = useRef(null);
    const autoRetryLastKeyRef = useRef(null);
    // Keep the caller's post-retry refresh callback in a ref so the
    // effect below always sees the latest closure without needing to
    // re-run (which would defeat batchKey deduping).
    const onMD5AutoRetryCompleteRef = useRef(onMD5AutoRetryComplete);
    useEffect(() => { onMD5AutoRetryCompleteRef.current = onMD5AutoRetryComplete; }, [onMD5AutoRetryComplete]);
    // Same ref trick for the post-completion refresh callback. The parent
    // passes a fresh `onComplete` (fetchData) identity every render; keeping
    // it in a ref lets `recoverFinalizingSessions` stay referentially stable
    // so its mount effect in <ChunkedUploader> fires once per mount instead
    // of re-polling /pending on every render.
    const onCompleteRef = useRef(onComplete);
    useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

    /**
     * When every upload in the current batch reaches a terminal state
     * AND at least one COMPLETED file has landed, fire the backend's
     * automatic MD5 retry endpoint once. The backend itself is idempotent
     * (files carry an `md5_auto_retry_attempted` flag), but debouncing
     * here avoids an unnecessary round-trip on every tick.
     */
    useEffect(() => {
        if (!orderId || uploads.length === 0) return;

        const TERMINAL = new Set([
            UPLOAD_STATUS.COMPLETED,
            UPLOAD_STATUS.FAILED,
            UPLOAD_STATUS.ABORTED,
        ]);
        const allTerminal = uploads.every(u => TERMINAL.has(u.status));
        const hasCompleted = uploads.some(u => u.status === UPLOAD_STATUS.COMPLETED);
        if (!allTerminal || !hasCompleted) return;

        // Signature that changes whenever the terminal-state list changes.
        // Using file ids + statuses ensures we don't re-trigger until a
        // NEW upload joins (or an existing one changes final state).
        const batchKey = uploads
            .map(u => `${u.fileId || u.id}:${u.status}`)
            .sort()
            .join('|');
        if (batchKey === autoRetryLastKeyRef.current) return;
        autoRetryLastKeyRef.current = batchKey;

        // Debounce so multiple state updates in the same render pass
        // collapse into a single POST.
        if (autoRetryTimerRef.current) {
            clearTimeout(autoRetryTimerRef.current);
        }
        autoRetryTimerRef.current = setTimeout(() => {
            const token = localStorage.getItem('token');
            axios.post(
                `${API}/orders/${orderId}/master-files/auto-retry-missing-md5`,
                {},
                { headers: token ? { Authorization: `Bearer ${token}` } : {} },
            ).then((res) => {
                // Backend already ran `process_uploaded_master` after each
                // regenerated MD5 — the sidecar summary + per-master
                // md5_verified verdict are up-to-date in Mongo. Tell the
                // caller so the order files list is re-fetched and the UI
                // reflects "MD5 verified" / summary "4/4" without a manual
                // "Verify" click. Only fires once per settled batch: the
                // `batchKey` guard above prevents any re-trigger, and the
                // backend `md5_auto_retry_attempted` flag makes the
                // endpoint itself idempotent on subsequent tabs / clients.
                const summary = res?.data || {};
                const changed =
                    (summary.files_regenerated || 0) > 0
                    || (summary.files_attempted || 0) > 0;
                if (changed && typeof onMD5AutoRetryCompleteRef.current === 'function') {
                    try { onMD5AutoRetryCompleteRef.current(summary); }
                    catch (e) { console.warn('[MD5] onMD5AutoRetryComplete threw', e); }
                }
            }).catch((err) => {
                // Non-critical — manual "Generate MD5" action remains
                // available for admins.
                // eslint-disable-next-line no-console
                console.warn('[MD5] auto-retry POST failed:', err?.message || err);
            });
        }, 400);

        return () => {
            if (autoRetryTimerRef.current) {
                clearTimeout(autoRetryTimerRef.current);
                autoRetryTimerRef.current = null;
            }
        };
    }, [uploads, orderId]);

    /**
     * Get auth token
     */
    const getToken = useCallback(() => {
        return localStorage.getItem('token');
    }, []);
    
    /**
     * Initialize upload session with backend
     */
    const initUploadSession = useCallback(async (file, chunkSize) => {
        const token = getToken();
        const response = await axios.post(
            `${API}/files/upload/chunked/init`,
            {
                order_id: orderId,
                filename: file.name,
                file_size: file.size,
                content_type: file.type || 'application/octet-stream',
                category: category,
                chunk_size: chunkSize  // Send chunk size so backend calculates correct number of chunks
            },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    }, [orderId, category, getToken]);
    
    /**
     * Get presigned URL for a chunk. The backend may also reply with a
     * `checksum_algorithm` hint (e.g. "SHA1") when the S3 multipart upload
     * was initiated with full-object checksum tracking — when present the
     * caller MUST have computed the SHA1 of the chunk BEFORE calling this
     * function and pass it as `checksumSha1`. The backend includes the
     * value in the signed URL so the eventual PUT (with the matching
     * `x-amz-checksum-sha1` header) passes S3 sigv4 validation.
     */
    const getChunkPresignedUrl = useCallback(async (sessionId, partNumber, checksumSha1 = null) => {
        const token = getToken();
        const response = await axios.post(
            `${API}/files/upload/chunked/presign`,
            {
                upload_session_id: sessionId,
                part_number: partNumber,
                ...(checksumSha1 ? { checksum_sha1: checksumSha1 } : {}),
            },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return {
            url: response.data.upload_url,
            checksumAlgorithm: response.data.checksum_algorithm || null,
        };
    }, [getToken]);
    
    /**
     * Notify backend that a chunk was uploaded. `checksumSha1` is the
     * per-part SHA1 echoed by S3 (`x-amz-checksum-sha1`) when the upload was
     * initiated with ChecksumAlgorithm=SHA1; backend includes it in the
     * final CompleteMultipartUpload call.
     */
    const markChunkUploaded = useCallback(async (sessionId, partNumber, etag, size, checksumSha1 = null) => {
        const token = getToken();
        await axios.post(
            `${API}/files/upload/chunked/part-uploaded`,
            {
                upload_session_id: sessionId,
                part_number: partNumber,
                etag: etag,
                size: size,
                ...(checksumSha1 ? { checksum_sha1: checksumSha1 } : {}),
            },
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }, [getToken]);
    
    /**
     * Complete the upload, optionally including a browser-computed MD5
     * (full-file hash) that backend stores alongside S3's native SHA1.
     */
    const completeUpload = useCallback(async (sessionId, md5Checksum = null) => {
        const token = getToken();
        const response = await axios.post(
            `${API}/files/upload/chunked/complete`,
            {
                upload_session_id: sessionId,
                ...(md5Checksum ? { md5_checksum: md5Checksum } : {}),
            },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    }, [getToken]);

    /**
     * Poll an upload session until it reaches a terminal state. Finalization is
     * backend-owned and durable, so the result survives tab close / refresh /
     * disconnect — we just watch for it. Transient polling errors are ignored.
     */
    const pollSessionUntilDone = useCallback(async (sessionId) => {
        const token = getToken();
        const deadline = Date.now() + 20 * 60 * 1000; // 20 min safety cap
        while (Date.now() < deadline) {
            await new Promise(r => setTimeout(r, 3000));
            try {
                const res = await axios.get(
                    `${API}/files/upload/chunked/session/${sessionId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const s = res.data;
                if (s.status === 'completed') return { status: 'completed', file_id: s.file_id };
                if (s.status === 'failed') {
                    return { status: 'failed', failure_reason: s.failure_reason, needs_restart: s.needs_restart };
                }
                if (s.status === 'aborted') {
                    return { status: 'failed', failure_reason: 'Upload was aborted.' };
                }
                // pending / uploading / completing → keep polling
            } catch (e) {
                // transient network error during polling — keep trying
            }
        }
        return { status: 'timeout' };
    }, [getToken]);
    
    /**
     * Update upload state
     */
    const updateUpload = useCallback((uploadId, updates) => {
        setUploads(prev => prev.map(u => 
            u.id === uploadId ? { ...u, ...updates } : u
        ));
        
        // Also update status ref for checking in async functions
        if (updates.status) {
            statusRefs.current.set(uploadId, updates.status);
        }
    }, []);
    
    /**
     * Process upload queue for a file with adaptive chunk sizing
     * Chunks start small for quick UI feedback, then scale up for throughput
     */
    const processUpload = useCallback(async (uploadId, file, session, existingUploadedParts = [], profile = null, resumeBytes = 0) => {
        // IMMUTABLE chunk plan for this session. Prefer the authoritative size
        // the backend recorded at init (returned on init and on resume) so the
        // client and server always agree on the part->byte mapping.
        const chunkSize = session.chunk_size || getFixedChunkSize(file.size);
        const maxConcurrent = getConcurrency(file.size);
        const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));

        // Track if proxy was used for THIS upload only (non-sticky)
        let useProxyForThisUpload = false;

        // Track total bytes uploaded (for progress only — never for chunk sizing)
        let totalUploadedBytes = resumeBytes;

        console.log(`Immutable chunk plan: ${(chunkSize/1024/1024).toFixed(0)}MB x ${totalChunks} parts, concurrency ${maxConcurrent}`);

        // Initialize with existing uploaded parts (for resume)
        const alreadyUploaded = new Set(existingUploadedParts);
        if (alreadyUploaded.size > 0) {
            console.log(`Resuming with ${alreadyUploaded.size}/${totalChunks} parts, ${(totalUploadedBytes/1024/1024).toFixed(1)}MB already uploaded`);
        }
        
        const uploadState = {
            uploadedParts: alreadyUploaded,
            uploadedBytes: totalUploadedBytes,
            startTime: Date.now(),
            chunkSize,
        };
        uploadRefs.current.set(uploadId, uploadState);
        
        // Initialize status ref
        statusRefs.current.set(uploadId, UPLOAD_STATUS.UPLOADING);

        // Create abort controller
        const abortController = new AbortController();
        abortControllers.current.set(uploadId, abortController);

        // Spin up the MD5 Web Worker IN PARALLEL with the upload. The worker
        // streams the file through SparkMD5 in 8MB chunks so we don't lock
        // the UI thread, and posts a final `{ status: 'done', md5 }` message
        // when the whole file has been hashed. We await its promise just
        // before calling `completeUpload` below so the MD5 ends up on the
        // saved file record. On hash failure we still complete the upload —
        // checksums are auxiliary, not blocking.
        //
        // Exception: `.md5` sidecars are themselves checksum manifests and
        // never get hashed — there is nothing to verify against and the
        // backend parses their content directly. Skipping the worker also
        // avoids a useless "MD5: af5e…" badge that would otherwise sit next
        // to the manifest's parsed entries.
        const isMd5Sidecar = (file.name || '').toLowerCase().endsWith('.md5');
        let md5Promise = Promise.resolve(null);
        let md5Worker = null;
        if (isMd5Sidecar) {
            updateUpload(uploadId, { md5Status: 'skipped' });
        } else {
        try {
            md5Worker = new Worker('/md5-worker.js');
            md5Promise = new Promise((resolve) => {
                // Safety net: if the worker never reports `done` (e.g. silent
                // CSP block on `/spark-md5.min.js`, browser killed worker,
                // FileReader OOM), we still resolve to null after a generous
                // timeout so completeUpload() is never stuck forever.
                // Budget: 30s minimum + 5s/100MB of file. A 1GB IMAGE.DAT
                // gets ~80s which is much longer than realistic hashing time.
                const timeoutMs = Math.max(30000, 30000 + (file.size / (100 * 1024 * 1024)) * 5000);
                const watchdog = setTimeout(() => {
                    console.warn(`[MD5] worker watchdog fired for ${file.name} after ${timeoutMs}ms — resolving null`);
                    updateUpload(uploadId, { md5Status: 'failed' });
                    resolve(null);
                }, timeoutMs);

                md5Worker.onmessage = (e) => {
                    const msg = e.data || {};
                    if (msg.status === 'progress') {
                        updateUpload(uploadId, {
                            md5Status: 'calculating',
                            md5Progress: msg.progress,
                        });
                    } else if (msg.status === 'done') {
                        clearTimeout(watchdog);
                        updateUpload(uploadId, {
                            md5Status: 'ready',
                            md5Progress: 1,
                            md5: msg.md5,
                        });
                        resolve(msg.md5);
                    } else if (msg.status === 'error') {
                        clearTimeout(watchdog);
                        console.warn(`[MD5] worker failed for ${file.name}:`, msg.error);
                        updateUpload(uploadId, { md5Status: 'failed' });
                        resolve(null);
                    }
                };
                md5Worker.onerror = (err) => {
                    clearTimeout(watchdog);
                    console.warn(`[MD5] worker error event for ${file.name}:`, err && (err.message || err.filename) || err);
                    updateUpload(uploadId, { md5Status: 'failed' });
                    resolve(null);
                };
                updateUpload(uploadId, { md5Status: 'calculating', md5Progress: 0 });
                md5Worker.postMessage({ file });
            });
        } catch (err) {
            console.warn('[MD5] Could not start worker; continuing without MD5:', err);
            updateUpload(uploadId, { md5Status: 'failed' });
        }
        }  // end if (!isMd5Sidecar)
        
        console.log(`Starting upload: ${file.name}, size: ${(file.size/1024/1024).toFixed(1)}MB`);
        
        /**
         * Upload a single chunk with current tier settings
         */
        const uploadChunkAdaptive = async (startByte, endByte, partNumber) => {
            const chunk = file.slice(startByte, endByte);
            const chunkSize = chunk.size;
            
            // If proxy already triggered for this upload, use it
            if (useProxyForThisUpload) {
                return uploadChunkViaProxyDirect(chunk, session.upload_session_id, partNumber, abortController.signal, chunkSize);
            }
            
            // Try direct S3 upload first
            for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
                try {
                    // For master/audio uploads, S3 was configured with
                    // ChecksumAlgorithm=SHA1. We must compute the chunk's
                    // SHA1 BEFORE asking the backend for a presigned URL so
                    // that the URL signs the exact `x-amz-checksum-sha1`
                    // header we'll send on the PUT. (S3 v4 sigs reject the
                    // header if it wasn't part of the signing scope.)
                    //
                    // Cheap pre-fetch: peek at the in-memory session record
                    // for the algorithm hint. If the value is missing we just
                    // do a regular non-checksum upload.
                    let partSha1B64 = null;
                    const putHeaders = { 'Content-Type': 'application/octet-stream' };

                    // We optimistically hash, then call presign — backend
                    // ignores the value when checksum isn't enabled.
                    // Marginal cost (~50ms for 5MB chunk) only pays once.
                    try {
                        const arrayBuffer = await chunk.arrayBuffer();
                        const digest = await crypto.subtle.digest('SHA-1', arrayBuffer);
                        const bytes = new Uint8Array(digest);
                        let binary = '';
                        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
                        partSha1B64 = btoa(binary);
                    } catch (e) {
                        console.warn('[Upload] Failed to compute chunk SHA1, will upload without checksum:', e);
                        partSha1B64 = null;
                    }

                    const { url: uploadUrl, checksumAlgorithm } = await getChunkPresignedUrl(
                        session.upload_session_id, partNumber, partSha1B64,
                    );
                    if (checksumAlgorithm === 'SHA1' && partSha1B64) {
                        putHeaders['x-amz-checksum-sha1'] = partSha1B64;
                        // S3 signs BOTH headers when ChecksumAlgorithm is set
                        // on the presign call, so we have to echo both back
                        // or the SignatureDoesNotMatch error is raised.
                        putHeaders['x-amz-sdk-checksum-algorithm'] = 'SHA1';
                    } else {
                        partSha1B64 = null;  // not part of final S3 complete
                    }

                    const response = await fetch(uploadUrl, {
                        method: 'PUT',
                        body: chunk,
                        signal: abortController.signal,
                        headers: putHeaders,
                    });

                    if (!response.ok) {
                        throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
                    }

                    const etag = response.headers.get('etag') || response.headers.get('ETag') || '';
                    await markChunkUploaded(session.upload_session_id, partNumber, etag, chunkSize, partSha1B64);

                    return { partNumber, etag, size: chunkSize, direct: true };
                } catch (error) {
                    if (error.name === 'AbortError') throw error;
                    
                    const isCorsOrNetworkError = (
                        error.message?.toLowerCase().includes('cors') ||
                        error.message?.includes('NetworkError') ||
                        error.message?.includes('Failed to fetch') ||
                        error.message?.includes('Load failed') ||
                        error.name === 'TypeError'
                    );
                    
                    if (isCorsOrNetworkError) {
                        console.warn(`CORS/Network error on chunk ${partNumber} - switching to proxy`);
                        useProxyForThisUpload = true;
                        return uploadChunkViaProxyDirect(chunk, session.upload_session_id, partNumber, abortController.signal, chunkSize);
                    }
                    
                    if (attempt < MAX_RETRIES - 1) {
                        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (attempt + 1)));
                    }
                }
            }
            
            // All retries failed, try proxy
            console.warn(`All direct attempts failed for chunk ${partNumber} - trying proxy`);
            useProxyForThisUpload = true;
            return uploadChunkViaProxyDirect(chunk, session.upload_session_id, partNumber, abortController.signal, chunkSize);
        };
        
        /**
         * Direct proxy upload helper
         */
        const uploadChunkViaProxyDirect = async (chunkBlob, sessionId, partNumber, signal, chunkSize) => {
            const token = getToken();
            const formData = new FormData();
            formData.append('upload_session_id', sessionId);
            formData.append('part_number', partNumber.toString());
            formData.append('chunk', chunkBlob, 'chunk.bin');
            
            console.log(`[PROXY] Chunk ${partNumber}, size: ${(chunkSize/1024/1024).toFixed(1)}MB`);
            
            const response = await axios.post(
                `${API}/files/upload/chunked/proxy-upload`,
                formData,
                {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    },
                    signal
                }
            );
            
            return { 
                partNumber, 
                etag: response.data.etag, 
                size: response.data.size,
                direct: false 
            };
        };
        
        try {
            const activeUploads = new Set();
            const uploadedPartsSet = new Set(existingUploadedParts);
            let anyPartFailed = false;

            // Deterministic queue of the parts still to upload. Part N always
            // covers the same bytes, so resume simply skips parts already done.
            const partQueue = [];
            for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
                if (!uploadedPartsSet.has(partNumber)) partQueue.push(partNumber);
            }
            let queueIndex = 0;

            while (queueIndex < partQueue.length || activeUploads.size > 0) {
                // Check if paused or aborted
                const currentStatus = statusRefs.current.get(uploadId);
                if (currentStatus === UPLOAD_STATUS.PAUSED || currentStatus === UPLOAD_STATUS.ABORTED) {
                    console.log(`Upload ${uploadId} stopped: ${currentStatus}`);
                    break;
                }

                // Start new uploads up to concurrency limit
                while (activeUploads.size < maxConcurrent && queueIndex < partQueue.length) {
                    const partNumber = partQueue[queueIndex++];
                    const startByte = (partNumber - 1) * chunkSize;
                    const endByte = Math.min(startByte + chunkSize, file.size);

                    const uploadPromise = uploadChunkAdaptive(startByte, endByte, partNumber).then(result => {
                        activeUploads.delete(uploadPromise);
                        
                        if (result) {
                            uploadedPartsSet.add(result.partNumber);
                            totalUploadedBytes += result.size;
                            
                            // Update progress
                            const uploadState = uploadRefs.current.get(uploadId);
                            if (uploadState) {
                                uploadState.uploadedParts.add(result.partNumber);
                                uploadState.uploadedBytes = totalUploadedBytes;
                                
                                const progress = (totalUploadedBytes / file.size) * 100;
                                const speed = calculateSpeed(uploadState.startTime, totalUploadedBytes);
                                const eta = calculateETA(speed, file.size - totalUploadedBytes);
                                
                                updateUpload(uploadId, {
                                    uploadedBytes: totalUploadedBytes,
                                    uploadedChunks: uploadedPartsSet.size,
                                    progress: Math.min(progress, 99.9),
                                    speed,
                                    eta
                                });
                            }
                        } else {
                            anyPartFailed = true;
                        }
                        
                        return result;
                    }).catch(error => {
                        activeUploads.delete(uploadPromise);
                        console.error(`Chunk ${partNumber} failed:`, error);
                        anyPartFailed = true;
                        return null;
                    });
                    
                    activeUploads.add(uploadPromise);
                }
                
                // Wait for at least one upload to complete
                if (activeUploads.size > 0) {
                    try {
                        await Promise.race(activeUploads);
                    } catch (e) {
                        // Ignore errors here, we'll check completion status later
                    }
                }
            }
            
            // Check final status
            const finalStatus = statusRefs.current.get(uploadId);
            if (finalStatus === UPLOAD_STATUS.ABORTED) {
                return null;
            }
            
            // Wait for any remaining active uploads to finish
            if (activeUploads.size > 0) {
                console.log(`Waiting for ${activeUploads.size} remaining uploads to complete...`);
                try {
                    await Promise.all(activeUploads);
                } catch (error) {
                    if (error.name !== 'AbortError') {
                        console.error('Error waiting for uploads:', error);
                    }
                }
            }
            
            // Strict completion gate: EVERY part in the immutable plan must be
            // uploaded. No byte-sum tolerance — the backend re-validates against
            // S3 ListParts before assembling, so partial sets are never accepted.
            if (uploadedPartsSet.size >= totalChunks && !anyPartFailed) {
                console.log(`All ${totalChunks} parts uploaded (${(totalUploadedBytes/1024/1024).toFixed(1)}MB), finalizing...`);
                // Transfer is done; show a clear finalizing state at 100% rather
                // than parking at 99.9% while S3 assembles the object.
                updateUpload(uploadId, { status: UPLOAD_STATUS.COMPLETING, progress: 100 });
                
                try {
                    // Await the in-flight MD5 hash (if the worker is still
                    // running). For very large masters the upload may finish
                    // first; in that case we wait the extra few seconds for
                    // MD5 to land. A null/failed hash is acceptable — we
                    // still complete the upload, S3's SHA1 already gives us
                    // an integrity check.
                    let md5Hex = null;
                    try { md5Hex = await md5Promise; } catch { md5Hex = null; }

                    // Kick off durable, backend-owned finalization. This returns
                    // immediately (202, status 'completing'); the server finishes
                    // S3 assembly + record creation independently of this request,
                    // so the customer can safely close the tab / lose connection.
                    const completeResp = await completeUpload(session.upload_session_id, md5Hex);

                    const finishCompleted = (fileId, extra = {}) => {
                        updateUpload(uploadId, {
                            status: UPLOAD_STATUS.COMPLETED,
                            progress: 100,
                            fileId,
                            md5: extra.md5 ?? md5Hex ?? null,
                            sha1: extra.sha1 ?? null,
                            md5Status: isMd5Sidecar
                                ? 'skipped'
                                : (((extra.md5 ?? md5Hex)) ? 'ready' : 'failed'),
                        });
                        // The file now shows in the disc's permanent list — refresh
                        // it and briefly show "Complete" before dropping this entry.
                        if (onComplete) onComplete(extra.fileResult);
                        setTimeout(() => {
                            setUploads(prev => prev.filter(u => u.id !== uploadId));
                            statusRefs.current.delete(uploadId);
                        }, 2500);
                        if (md5Worker) { try { md5Worker.terminate(); } catch { /* ignore */ } }
                    };

                    // Idempotent fast-path: an already-completed session returns its
                    // stored file record directly (has an `id`).
                    if (completeResp && completeResp.id) {
                        finishCompleted(completeResp.id, {
                            md5: completeResp.md5_checksum || md5Hex || null,
                            sha1: completeResp.sha1_checksum || null,
                            fileResult: completeResp,
                        });
                        return completeResp;
                    }

                    // Normal path: poll until the backend reports the terminal state.
                    const result = await pollSessionUntilDone(session.upload_session_id);
                    if (result.status === 'completed') {
                        finishCompleted(result.file_id);
                        return result;
                    }
                    if (result.status === 'failed') {
                        updateUpload(uploadId, {
                            status: UPLOAD_STATUS.FAILED,
                            error: result.failure_reason || 'Finalization failed',
                            needsRestart: !!result.needs_restart,
                            retryableFinalize: !result.needs_restart,
                        });
                        if (md5Worker) { try { md5Worker.terminate(); } catch { /* ignore */ } }
                        return null;
                    }
                    // timeout — finalization is still running server-side (the cron
                    // sweep will finish it). Leave a retryable state to re-check.
                    updateUpload(uploadId, {
                        status: UPLOAD_STATUS.FAILED,
                        error: 'Finalization is taking longer than expected. It will finish in the background — refresh to check, or retry.',
                        retryableFinalize: true,
                    });
                    if (md5Worker) { try { md5Worker.terminate(); } catch { /* ignore */ } }
                    return null;
                } catch (completeError) {
                    console.error('Failed to complete upload:', completeError);
                    const status = completeError.response?.status;
                    const errorDetail = completeError.response?.data?.detail || completeError.message;
                    // 409 => the S3 multipart upload is gone; a fresh upload is
                    // required. 503 => transient, retrying finalization is safe.
                    const needsRestart = status === 409;
                    const retryable = status === 503;
                    updateUpload(uploadId, { 
                        status: UPLOAD_STATUS.FAILED,
                        error: errorDetail,
                        needsRestart,
                        retryableFinalize: retryable,
                    });
                    throw completeError;
                }
            } else {
                const missing = totalChunks - uploadedPartsSet.size;
                console.error(`Upload incomplete: ${uploadedPartsSet.size}/${totalChunks} parts uploaded`);
                updateUpload(uploadId, { 
                    status: UPLOAD_STATUS.FAILED,
                    error: `Upload incomplete: ${missing} of ${totalChunks} part(s) failed to transfer. You can retry to upload the missing parts.`,
                    retryableFinalize: true,
                });
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log(`Upload ${uploadId} was aborted`);
                return null;
            }
            
            console.error(`Upload ${uploadId} failed:`, error);
            updateUpload(uploadId, {
                status: UPLOAD_STATUS.FAILED,
                error: error.message || 'Upload failed'
            });
            throw error;
        } finally {
            uploadRefs.current.delete(uploadId);
            abortControllers.current.delete(uploadId);
        }
        
        return null;
    }, [getToken, getChunkPresignedUrl, markChunkUploaded, completeUpload, pollSessionUntilDone, updateUpload, onComplete]);
    
    /**
     * Start uploading a file - checks for existing session to resume
     */
    const startUpload = useCallback(async (file) => {
        const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // One immutable chunk size for the whole file (and all resumes).
        const initialChunkSize = getFixedChunkSize(file.size);
        const totalChunks = Math.max(1, Math.ceil(file.size / initialChunkSize));

        console.log(`Starting upload for file: ${file.name}, size: ${(file.size/1024/1024).toFixed(1)}MB`);
        console.log(`Fixed chunk plan: ${(initialChunkSize/1024/1024).toFixed(0)}MB x ${totalChunks} parts`);
        
        // Add to uploads list
        const newUpload = {
            id: uploadId,
            file: file,
            filename: file.name,
            fileSize: file.size,
            uploadedBytes: 0,
            uploadedChunks: 0,
            totalChunks: totalChunks,
            progress: 0,
            status: UPLOAD_STATUS.QUEUED,
            speed: 0,
            eta: null,
            error: null,
            sessionId: null,
            fileId: null,
            profile: 'fixed'
        };
        
        setUploads(prev => [...prev, newUpload]);
        statusRefs.current.set(uploadId, UPLOAD_STATUS.QUEUED);
        
        try {
            // Check for existing pending session for this file
            const token = getToken();
            let session = null;
            let existingParts = [];
            let resumeFromBytes = 0;
            let finalizingMatch = null;
            
            try {
                const pendingRes = await axios.get(
                    `${API}/files/upload/chunked/order/${orderId}/pending`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                
                const pendingSessions = pendingRes.data || [];

                // A session already finalizing for this exact file must NOT be
                // re-uploaded — that would create a duplicate. Attach + poll it.
                finalizingMatch = pendingSessions.find(
                    s => s.filename === file.name && s.file_size === file.size && s.status === 'completing'
                ) || null;

                // Otherwise resume an in-progress ('uploading') session, picking
                // the one with the most progress.
                const matchingSessions = finalizingMatch ? [] : pendingSessions.filter(
                    s => s.filename === file.name && s.file_size === file.size && s.status === 'uploading'
                );
                
                // Sort by progress (highest first) and pick the best one
                matchingSessions.sort((a, b) => b.progress_percent - a.progress_percent);
                const matchingSession = matchingSessions[0];
                
                if (matchingSession) {
                    console.log(`Found existing session: ${matchingSession.upload_session_id}`);
                    console.log(`Progress: ${matchingSession.progress_percent}% (${matchingSession.uploaded_chunks} parts)`);
                    
                    // Get full session details with parts
                    const sessionRes = await axios.get(
                        `${API}/files/upload/chunked/session/${matchingSession.upload_session_id}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    
                    session = {
                        upload_session_id: sessionRes.data.upload_session_id,
                        total_chunks: sessionRes.data.total_chunks,
                        chunk_size: sessionRes.data.chunk_size,
                    };
                    
                    // Resume by PART NUMBER only. The immutable chunk plan means
                    // each uploaded part maps to a fixed byte range, so we skip
                    // exactly those parts and never recompute offsets from bytes.
                    const parts = sessionRes.data.parts || [];
                    existingParts = parts.map(p => p.PartNumber);
                    const sessionChunk = sessionRes.data.chunk_size || getFixedChunkSize(file.size);
                    // Recompute resumed bytes from the fixed plan (not the stored
                    // part sizes) so progress stays accurate even for legacy data.
                    resumeFromBytes = existingParts.reduce((sum, pn) => {
                        const start = (pn - 1) * sessionChunk;
                        return sum + Math.min(sessionChunk, file.size - start);
                    }, 0);
                    
                    console.log(`Resuming from ${existingParts.length} parts, ${(resumeFromBytes/1024/1024).toFixed(1)}MB uploaded`);
                    
                    // Update UI with resume state
                    updateUpload(uploadId, {
                        sessionId: session.upload_session_id,
                        uploadedBytes: resumeFromBytes,
                        uploadedChunks: existingParts.length,
                        progress: (resumeFromBytes / file.size) * 100,
                        status: UPLOAD_STATUS.UPLOADING
                    });
                }
            } catch (e) {
                console.log('No existing session found, starting fresh');
            }
            
            // A finalizing session for this file is already backend-owned: attach
            // to it and poll to completion instead of starting a duplicate upload.
            if (finalizingMatch) {
                console.log(`File already finalizing (${finalizingMatch.upload_session_id}); attaching + polling.`);
                updateUpload(uploadId, {
                    sessionId: finalizingMatch.upload_session_id,
                    uploadedBytes: file.size,
                    uploadedChunks: finalizingMatch.uploaded_chunks,
                    progress: 100,
                    status: UPLOAD_STATUS.COMPLETING,
                });
                statusRefs.current.set(uploadId, UPLOAD_STATUS.COMPLETING);
                const result = await pollSessionUntilDone(finalizingMatch.upload_session_id);
                if (result.status === 'completed') {
                    updateUpload(uploadId, { status: UPLOAD_STATUS.COMPLETED, progress: 100, fileId: result.file_id });
                    if (onComplete) onComplete();
                    setTimeout(() => {
                        setUploads(prev => prev.filter(u => u.id !== uploadId));
                        statusRefs.current.delete(uploadId);
                    }, 2500);
                } else if (result.status === 'failed') {
                    updateUpload(uploadId, {
                        status: UPLOAD_STATUS.FAILED,
                        error: result.failure_reason || 'Finalization failed',
                        needsRestart: !!result.needs_restart,
                        retryableFinalize: !result.needs_restart,
                    });
                } else {
                    updateUpload(uploadId, {
                        status: UPLOAD_STATUS.FAILED,
                        error: 'Finalization is taking longer than expected. Refresh to check, or retry.',
                        retryableFinalize: true,
                    });
                }
                return;
            }
            
            // If no existing session, create new one
            if (!session) {
                console.log(`Initializing new upload session...`);
                const initRes = await initUploadSession(file, initialChunkSize);
                session = initRes;
                console.log(`Session created:`, session);
                
                setUploads(prev => prev.map(u => 
                    u.id === uploadId 
                        ? { ...u, sessionId: session.upload_session_id, status: UPLOAD_STATUS.UPLOADING }
                        : u
                ));
            }
            
            statusRefs.current.set(uploadId, UPLOAD_STATUS.UPLOADING);
            
            // Start processing (skips parts already uploaded; immutable chunk plan)
            await processUpload(uploadId, file, session, existingParts, null, resumeFromBytes);
        } catch (error) {
            console.error('Failed to start upload:', error);
            updateUpload(uploadId, { 
                status: UPLOAD_STATUS.FAILED,
                error: error.response?.data?.detail || error.message || 'Failed to start upload'
            });
            statusRefs.current.set(uploadId, UPLOAD_STATUS.FAILED);
        }
    }, [orderId, getToken, initUploadSession, processUpload, updateUpload, pollSessionUntilDone, onComplete]);
    
    /**
     * Pause an upload
     */
    const pauseUpload = useCallback((uploadId) => {
        statusRefs.current.set(uploadId, UPLOAD_STATUS.PAUSED);
        const controller = abortControllers.current.get(uploadId);
        if (controller) {
            controller.abort();
        }
        updateUpload(uploadId, { status: UPLOAD_STATUS.PAUSED });
    }, [updateUpload]);
    
    /**
     * Resume an upload
     */
    const resumeUpload = useCallback(async (uploadId) => {
        const upload = uploads.find(u => u.id === uploadId);
        if (!upload || !upload.sessionId) return;
        
        updateUpload(uploadId, { status: UPLOAD_STATUS.RESUMING });
        statusRefs.current.set(uploadId, UPLOAD_STATUS.RESUMING);
        
        // Get session status from backend
        try {
            const token = getToken();
            const response = await axios.get(
                `${API}/files/upload/chunked/session/${upload.sessionId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const session = response.data;
            
            // Extract uploaded part numbers from the parts array
            const uploadedParts = (session.parts || []).map(p => p.PartNumber);
            console.log(`Resume: ${uploadedParts.length}/${session.total_chunks} parts already uploaded: ${uploadedParts.join(', ')}`);
            
            // Resume processing with updated state
            updateUpload(uploadId, { 
                status: UPLOAD_STATUS.UPLOADING,
                uploadedBytes: session.uploaded_bytes,
                uploadedChunks: session.uploaded_chunks,
                progress: session.progress_percent
            });
            statusRefs.current.set(uploadId, UPLOAD_STATUS.UPLOADING);
            
            await processUpload(uploadId, upload.file, {
                upload_session_id: session.upload_session_id,
                total_chunks: session.total_chunks,
                chunk_size: session.chunk_size,
            }, uploadedParts);
        } catch (error) {
            console.error('Failed to resume upload:', error);
            updateUpload(uploadId, { 
                status: UPLOAD_STATUS.FAILED,
                error: error.response?.data?.detail || 'Failed to resume upload'
            });
            statusRefs.current.set(uploadId, UPLOAD_STATUS.FAILED);
        }
    }, [uploads, getToken, processUpload, updateUpload]);
    
    /**
     * Cancel/abort an upload
     */
    const cancelUpload = useCallback(async (uploadId) => {
        statusRefs.current.set(uploadId, UPLOAD_STATUS.ABORTED);
        const controller = abortControllers.current.get(uploadId);
        if (controller) {
            controller.abort();
        }
        
        const upload = uploads.find(u => u.id === uploadId);
        if (upload?.sessionId) {
            try {
                const token = getToken();
                await axios.post(
                    `${API}/files/upload/chunked/abort/${upload.sessionId}`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } catch (error) {
                console.error('Failed to abort upload on server:', error);
            }
        }
        
        updateUpload(uploadId, { status: UPLOAD_STATUS.ABORTED });
    }, [uploads, getToken, updateUpload]);
    
    /**
     * Remove upload from list
     */
    const removeUpload = useCallback((uploadId) => {
        setUploads(prev => prev.filter(u => u.id !== uploadId));
        statusRefs.current.delete(uploadId);
    }, []);

    /**
     * Recover any sessions that were still finalizing when the page was closed
     * / refreshed. Since finalization is backend-owned, we re-surface a
     * "Finalizing" entry and poll it to completion — no file handle needed and
     * no duplicate upload. Called once on mount by the uploader component.
     */
    const recoverFinalizingSessions = useCallback(async () => {
        if (!orderId) return;
        try {
            const token = getToken();
            const res = await axios.get(
                `${API}/files/upload/chunked/order/${orderId}/pending`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Only recover finalizing sessions that belong to THIS uploader's
            // category (e.g. master_slot_<id> / _layer0). The /pending endpoint
            // returns sessions for the whole order, so without this filter every
            // mounted disc uploader would surface another disc's finalizing
            // session and (via onActiveChange) pin its own disc open — expanding
            // unrelated discs. Category uniquely identifies the disc + layer.
            const sessions = (res.data || []).filter(s => s.status === 'completing' && s.category === category);
            for (const s of sessions) {
                // Skip if we're already tracking this session.
                let alreadyTracked = false;
                setUploads(prev => {
                    alreadyTracked = prev.some(u => u.sessionId === s.upload_session_id);
                    if (alreadyTracked) return prev;
                    return [...prev, {
                        id: `recover_${s.upload_session_id}`,
                        file: null,
                        filename: s.filename,
                        fileSize: s.file_size,
                        uploadedBytes: s.file_size,
                        uploadedChunks: s.uploaded_chunks,
                        totalChunks: s.total_chunks,
                        progress: 100,
                        status: UPLOAD_STATUS.COMPLETING,
                        speed: 0,
                        eta: null,
                        error: null,
                        sessionId: s.upload_session_id,
                        fileId: s.file_id,
                        profile: 'fixed',
                    }];
                });
                if (alreadyTracked) continue;
                const uploadId = `recover_${s.upload_session_id}`;
                statusRefs.current.set(uploadId, UPLOAD_STATUS.COMPLETING);
                pollSessionUntilDone(s.upload_session_id).then(result => {
                    if (result.status === 'completed') {
                        updateUpload(uploadId, { status: UPLOAD_STATUS.COMPLETED, progress: 100, fileId: result.file_id });
                        if (onCompleteRef.current) onCompleteRef.current();
                        setTimeout(() => {
                            setUploads(prev => prev.filter(u => u.id !== uploadId));
                            statusRefs.current.delete(uploadId);
                        }, 2500);
                    } else if (result.status === 'failed') {
                        updateUpload(uploadId, {
                            status: UPLOAD_STATUS.FAILED,
                            error: result.failure_reason || 'Finalization failed',
                            needsRestart: !!result.needs_restart,
                            retryableFinalize: !result.needs_restart,
                        });
                    }
                });
            }
        } catch (e) {
            // No pending sessions / transient error — nothing to recover.
        }
    }, [orderId, getToken, pollSessionUntilDone, updateUpload, category]);
    
    return {
        uploads,
        startUpload,
        pauseUpload,
        resumeUpload,
        cancelUpload,
        removeUpload,
        recoverFinalizingSessions,
        isUploading: uploads.some(u => 
            u.status === UPLOAD_STATUS.UPLOADING || 
            u.status === UPLOAD_STATUS.RESUMING ||
            u.status === UPLOAD_STATUS.COMPLETING
        )
    };
}
